/**
 * ChariAI - Main Application
 * ==========================
 * Root component with routing and global state management.
 * Optimized with React.lazy for code splitting.
 */

import { useState, useEffect, lazy, Suspense, memo } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { xhrGet, API } from "./utils/api";

// Eagerly loaded - critical path components
import { Generator } from "./components/Generator/Generator";

// Lazy loaded - secondary routes (code splitting)
const CookieConsentBanner = lazy(() => import("./components/CookieConsent").then(m => ({ default: m.CookieConsentBanner })));

// Lazy loaded - secondary routes (code splitting)
const AuthCallback = lazy(() => import("./components/AuthCallback").then(m => ({ default: m.AuthCallback })));
const Dashboard = lazy(() => import("./components/Dashboard").then(m => ({ default: m.Dashboard })));
const PricingPage = lazy(() => import("./components/Pricing").then(m => ({ default: m.PricingPage })));
const AdminDashboard = lazy(() => import("./components/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const PaymentSuccess = lazy(() => import("./components/PaymentSuccess").then(m => ({ default: m.PaymentSuccess })));
const Settings = lazy(() => import("./components/Settings").then(m => ({ default: m.Settings })));
const Referrals = lazy(() => import("./components/Referrals").then(m => ({ default: m.Referrals })));
const ReferralRedirect = lazy(() => import("./components/ReferralRedirect").then(m => ({ default: m.ReferralRedirect })));
const LegalPage = lazy(() => import("./components/Legal").then(m => ({ default: m.LegalPage })));

// Loading fallback component
const PageLoader = memo(() => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-zinc-400">Loading...</span>
    </div>
  </div>
));

// Beta Banner Component
function BetaBanner() {
  // Just a subtle indicator - no dismissal needed since it's minimal
  return null; // Disabled - beta indicator moved to header
}

// Subtle beta badge for header - used in Generator component
export function BetaBadge() {
  return (
    <span className="text-[10px] font-bold text-amber-500/70 bg-amber-500/10 px-1.5 py-0.5 rounded uppercase tracking-wider">
      beta
    </span>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isGoogleCallback, setIsGoogleCallback] = useState(false);
  
  // User features and limits
  const [guestStatus, setGuestStatus] = useState({ 
    remaining: 2, 
    limit: 2, 
    features: null 
  });
  const [userFeatures, setUserFeatures] = useState(null);

  // Check for session_id in URL (Google OAuth callback)
  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      setIsGoogleCallback(true);
    }
  }, [location.hash]);

  // Check for payment success redirect and refresh user data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('payment') === 'success') {
      checkAuth();
      checkUserStatus();
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Check existing auth on mount — short-circuit for new visitors (no token = skip blocking call)
  useEffect(() => {
    if (isGoogleCallback) return;
    const hasToken = !!localStorage.getItem("session_token");
    if (!hasToken) {
      // New visitor: skip auth call, fire status check in idle, paint landing immediately
      setAuthChecked(true);
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
      idle(() => { checkUserStatus(); }, { timeout: 3000 });
    } else {
      checkAuth();
      checkUserStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleCallback]);

  // Refresh user data when navigating to key pages (e.g., after admin changes)
  useEffect(() => {
    const pagesToRefresh = ['/', '/dashboard', '/settings', '/pricing'];
    if (authChecked && user && pagesToRefresh.includes(location.pathname)) {
      // Refresh user data to catch any plan changes made in admin
      checkAuth();
      checkUserStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Heartbeat to track user activity (every 2 minutes) — deferred to idle
  useEffect(() => {
    if (!user) return;
    
    const sendHeartbeat = async () => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/auth/heartbeat`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        const token = localStorage.getItem("session_token");
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
        xhr.withCredentials = true;
        xhr.send();
      } catch (err) {
        // Silent fail for heartbeat
      }
    };

    // Defer initial heartbeat until browser is idle (after LCP)
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    const idleHandle = idle(sendHeartbeat, { timeout: 4000 });
    
    // Send heartbeat every 2 minutes
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);
    
    return () => {
      clearInterval(interval);
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
    };
  }, [user]);

  // Track anonymous visitors (non-logged-in users) — deferred to idle
  useEffect(() => {
    // Only track if user is not logged in and auth check is complete
    if (user || !authChecked) return;
    
    const trackVisitor = () => {
      try {
        // Get or create visitor ID
        let visitorId = localStorage.getItem("visitor_id");
        if (!visitorId) {
          visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem("visitor_id", visitorId);
        }
        
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/analytics/track`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.send(JSON.stringify({
          visitor_id: visitorId,
          page: location.pathname,
          referrer: document.referrer || null
        }));
      } catch (err) {
        // Silent fail for tracking
      }
    };

    // Defer until browser is idle so it doesn't compete with LCP
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    const idleHandle = idle(trackVisitor, { timeout: 5000 });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
    };
  }, [user, authChecked, location.pathname]);

  const checkAuth = async () => {
    try {
      const { status, data } = await xhrGet(`${API}/auth/me`);
      if (status === 200 && data) {
        setUser(data);
      }
    } catch (err) {
      console.error("Auth check error:", err);
    } finally {
      setAuthChecked(true);
    }
  };

  const checkUserStatus = async () => {
    try {
      const guestId = localStorage.getItem("guest_id") || `guest_${Date.now()}`;
      localStorage.setItem("guest_id", guestId);
      
      const { status, data } = await xhrGet(`${API}/auth/user-status`, { "X-Guest-ID": guestId });
      if (status === 200 && data) {
        setGuestStatus({
          remaining: data.remaining,
          limit: data.limit,
          used: data.used,
          features: data.features,
          long_form: data.long_form
        });
        setUserFeatures(data.features);
      }
    } catch (err) {
      console.error("Status check error:", err);
    }
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    checkUserStatus();
    if (userData?.is_admin) {
      navigate("/admin");
    }
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem("session_token");
      if (sessionToken) {
        await xhrGet(`${API}/auth/logout`);
      }
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("session_token");
      setUser(null);
      setUserFeatures(null);
      checkUserStatus();
      navigate("/");
    }
  };

  const handleGuestGenerate = () => {
    checkUserStatus();
  };

  // Show loading until auth is checked
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-zinc-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
    <BetaBanner />
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route 
          path="/" 
          element={
            <Generator 
              user={user} 
              onAuthSuccess={handleAuthSuccess} 
              onLogout={handleLogout} 
              guestStatus={guestStatus}
              onGuestGenerate={handleGuestGenerate}
              userFeatures={userFeatures}
            />
          } 
        />
        <Route 
          path="/auth/callback" 
          element={<AuthCallback onAuthSuccess={handleAuthSuccess} />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <Dashboard 
              user={user} 
              onLogout={handleLogout}
              onAuthSuccess={handleAuthSuccess}
            />
          } 
        />
        <Route 
          path="/pricing" 
          element={
            <PricingPage 
              user={user}
              onAuthSuccess={handleAuthSuccess}
              onLogout={handleLogout}
            />
          } 
        />
        <Route 
          path="/admin" 
          element={
            <AdminDashboard 
              user={user} 
              onLogout={handleLogout} 
            />
          } 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
        <Route 
          path="/settings" 
          element={
            <Settings 
              user={user}
              onLogout={handleLogout}
              onUserUpdate={handleUserUpdate}
            />
          } 
        />
        <Route 
          path="/payment/success" 
          element={
            <PaymentSuccess 
              onPaymentComplete={async () => {
                try {
                  const { status, data } = await xhrGet(`${API}/auth/me`);
                  if (status === 200 && data) {
                    setUser(data);
                  }
                  await checkUserStatus();
                } catch (err) {
                  console.error("Error refreshing user data:", err);
                }
              }}
            />
          } 
        />
        <Route 
          path="/payment/cancel" 
          element={
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Payment Cancelled</h1>
                <p className="text-zinc-400 mb-6">Your payment was cancelled. No charges were made.</p>
                <button
                  onClick={() => navigate("/pricing")}
                  className="px-6 py-3 bg-violet-600 hover:bg-violet-500 rounded-lg font-medium transition-colors"
                >
                  Return to Pricing
                </button>
              </div>
            </div>
          } 
        />
        <Route 
          path="/referrals" 
          element={
            <Referrals 
              user={user}
              onLogout={handleLogout}
            />
          } 
        />
        <Route 
          path="/r/:code" 
          element={<ReferralRedirect />} 
        />
        <Route 
          path="/legal/:page" 
          element={<LegalPage />} 
        />
        <Route 
          path="/legal" 
          element={<LegalPage />} 
        />
      </Routes>
    </Suspense>
    <Suspense fallback={null}>
      <CookieConsentBanner />
    </Suspense>
    </>
  );
}

export default App;
